import { useMemo } from 'react';
import Button from 'components/Button';
import PropTypes from 'prop-types';
import { useTranslation } from 'services/hooks';

import './style.css';

const CertificateProgress = ({ onClick, progress = 0 }) => {
	const getTranslation = useTranslation();

	const TRANSLATION_KEY = 'course';

	const userProgress = useMemo(() => {
		const progressValue = Math.min(Math.max(progress, 0), 100);
		return parseInt(progressValue);
	}, [progress]);

	const contentTitle = useMemo(() => {
		return userProgress >= 100
			? getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_complete_title`)
			: `${userProgress}% ${getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_title`)}`;
	}, [getTranslation]);

	const contentSubTitle = useMemo(() => {
		return userProgress >= 100
			? getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_complete_sub_title`)
			: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_sub_title`);
	}, [getTranslation]);

	return (
		<div className="CertificateProgress gray_box">
			<div className="CertificateProgress__content">
				<div className="CertificateProgress__circle_wrapper">
					<CircularProgressBar progress={userProgress} size={80} strokeWidth={5} color="#2fae85" />
				</div>
				<div className="CertificateProgress__text">
					<div className="CertificateProgress__content_title">{contentTitle}</div>
					<div className="CertificateProgress__content_subTitle">{contentSubTitle}</div>
					{userProgress === 100 && (
						<Button
							onClick={onClick}
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_certificate_button`)}
							small
							endIcon="rightArrow"
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default CertificateProgress;

export const CircularProgressBar = ({ size, progress, strokeWidth, color }) => {
	const viewBox = `0 0 ${size} ${size}`;
	const radius = (size - strokeWidth) / 2;
	const circumference = radius * Math.PI * 2;
	const dash = (progress * circumference) / 100;

	return (
		<svg className="svg" width={size} height={size} viewBox={viewBox}>
			<circle
				className="svg__circle-bg"
				fill="none"
				stroke="#ccc"
				cx={size / 2}
				cy={size / 2}
				r={radius}
				strokeWidth={`${strokeWidth}px`}
			/>
			<circle
				fill="none"
				stroke={color}
				cx={size / 2}
				cy={size / 2}
				r={radius}
				strokeWidth={`${strokeWidth}px`}
				transform={`rotate(-90 ${size / 2} ${size / 2})`}
				strokeDasharray={[dash, circumference - dash]}
				strokeLinecap="round"
				style={{ transition: 'all 0.5s' }}
			/>
			<svg width={size / 2} height={size / 2} x="21" y="21" viewBox="0 0 23 23">
				<defs>
					<path d="M.048 22.117V.047h22.168v22.07z" />
				</defs>
				<path
					fill="#2fae85"
					d="M13.463 15.477H8.801v-.53c0-1.912-.88-2.928-1.992-4.216C5.185 8.853 3.165 6.515 3.885.514L3.94.048h14.382l.056.466c.72 6.001-1.3 8.339-2.924 10.217-1.113 1.288-1.992 2.304-1.992 4.217zm-3.623-1.06h2.584c.153-1.977 1.164-3.146 2.23-4.379 1.52-1.758 3.236-3.744 2.725-8.931H4.885C4.374 6.294 6.091 8.28 7.61 10.038c1.066 1.233 2.077 2.402 2.23 4.38"
				/>
				<path
					fill="#2fae85"
					d="M6.38 10.23c-.456-.537-1.224-.899-2.037-1.281C2.429 8.049.048 6.93.048 2.815v-.53h2.71v1.06H1.124c.184 3.006 1.955 3.839 3.67 4.646.92.432 1.79.84 2.395 1.554z"
				/>
				<path
					fill="#2fae85"
					d="M15.883 10.23l-.807-.685c.605-.713 1.474-1.122 2.395-1.554 1.715-.807 3.486-1.64 3.67-4.647h-1.636V2.285h2.711v.53c0 4.115-2.381 5.234-4.295 6.134-.813.382-1.581.744-2.037 1.281"
				/>
				<path fill="#2fae85" d="M7.102 17.429v-1.06h8.06v1.06z" />
				<path fill="#2fae85" d="M6.041 19.396h10.182l.517 1.661H5.524zm10.961-1.059H5.262l-1.178 3.78H18.18z" />
				<path fill="#2fae85" d="M2.615 22.117v-1.06h17.034v1.06z" />
			</svg>
		</svg>
	);
};
CircularProgressBar.propTypes = {
	size: PropTypes.number.isRequired,
	progress: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	strokeWidth: PropTypes.number.isRequired,
};
